import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaYoutube, FaMapMarkerAlt, FaEnvelope, FaPhone, FaArrowRight } from 'react-icons/fa';

const Footer = () => {
    const { t } = useTranslation();

    return (
        <footer className="relative bg-[#122C56] text-white py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F253F] via-[#122C56] to-transparent opacity-90"></div>

            <div className="container max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative z-10">
                {/* Main Footer Content */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12 lg:gap-16 text-center md:text-left">
                    {/* Travel with Ashan Links */}
                    <div className="space-y-6">
                        <h5 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-6 tracking-tight uppercase drop-shadow-md">
                            {t('footerSection.ashanTours')}
                        </h5>
                        <ul className="space-y-4">
                            {[
                                { to: '/', label: t('home') },
                                { to: '/about-us', label: t('about') },
                                { to: '/srilanka', label: t('srilanka') },
                                { to: '/tour', label: t('tour') },
                                { to: '/excursions', label: t('excursions') },
                                { to: '/things', label: t('things') },
                                { to: '/contact-us', label: t('contact.navLabel') },
                            ].map((link) => (
                                <li key={link.to}>
                                    <NavLink
                                        to={link.to}
                                        className="text-gray-200 hover:text-[#C58E2F] text-base sm:text-lg font-medium transition-colors duration-300 ease-in-out hover:underline hover:underline-offset-4"
                                    >
                                        {link.label}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Get in Touch */}
                    <div className="space-y-6">
                        <h5 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-6 tracking-tight uppercase drop-shadow-md">
                            {t('footerSection.getInTouch')}
                        </h5>
                        <ul className="space-y-5">
                            <li className="flex items-center justify-center md:justify-start gap-3">
                                <FaMapMarkerAlt className="w-6 sm:w-7 h-6 sm:h-7 text-[#C58E2F] flex-shrink-0" />
                                <span className="text-base sm:text-lg font-medium text-gray-100 leading-relaxed">
                                    {t('footerSection.address')}
                                </span>
                            </li>
                            <li className="flex items-center justify-center md:justify-start gap-3">
                                <FaEnvelope className="w-6 sm:w-7 h-6 sm:h-7 text-[#C58E2F] flex-shrink-0" />
                                <a
                                    href="mailto:ashantourssl@gmail.com"
                                    className="text-base sm:text-lg font-medium text-gray-100 hover:text-[#C58E2F] transition-colors duration-300 ease-in-out hover:underline hover:underline-offset-4"
                                >
                                    {t('footerSection.email')}
                                </a>
                            </li>
                            <li className="flex items-center justify-center md:justify-start gap-3">
                                <FaPhone className="w-6 sm:w-7 h-6 sm:h-7 text-[#C58E2F] flex-shrink-0" />
                                <a
                                    href="tel:+94779590011"
                                    className="text-base sm:text-lg font-medium text-gray-100 hover:text-[#C58E2F] transition-colors duration-300 ease-in-out hover:underline hover:underline-offset-4"
                                >
                                    {t('footerSection.phone')}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Stay Connected */}
                    <div className="space-y-6">
                        <h5 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-6 tracking-tight uppercase drop-shadow-md">
                            {t('footerSection.stayConnected')}
                        </h5>
                        <div className="flex justify-center mb-8">
                            <div className="relative w-full max-w-[90%] sm:max-w-md">
                                <input
                                    type="email"
                                    className="w-full p-3 sm:p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C58E2F] text-gray-800 text-base sm:text-lg font-medium bg-[#F8F1E9] transition-all duration-300 placeholder-gray-500 shadow-sm hover:shadow-md"
                                    placeholder={t('footerSection.emailPlaceholder')}
                                    aria-label={t('footerSection.emailPlaceholder')}
                                />
                                <button className="absolute right-0 top-0 h-full px-5 sm:px-6 bg-[#C58E2F] text-white rounded-r-lg hover:bg-[#A57324] transition-colors duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#C58E2F] shadow-sm">
                                    <FaArrowRight className="w-6 sm:w-7 h-6 sm:h-7" />
                                </button>
                            </div>
                        </div>
                        <div className="flex justify-center gap-6 sm:gap-8">
                            {[
                                { href: 'https://www.facebook.com', icon: FaFacebookF, label: t('footerSection.socialMedia.facebook') },
                                { href: 'https://www.instagram.com/ashan.tours?igsh=ZW9qZDdmdnNrc3Z1&utm_source=qr', icon: FaInstagram, label: t('footerSection.socialMedia.instagram') },
                                { href: 'https://www.youtube.com', icon: FaYoutube, label: t('footerSection.socialMedia.youtube') },
                            ].map((social) => (
                                <a
                                    key={social.href}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-white hover:text-[#C58E2F] transition-colors duration-300 ease-in-out transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-[#C58E2F] rounded-full p-2"
                                    aria-label={social.label}
                                >
                                    <social.icon size={28} />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <hr className="my-8 sm:my-10 md:my-12 border-gray-400 opacity-50" />

                {/* Footer Bottom */}
                <div className="text-center space-y-4">
                    <p className="text-white text-base sm:text-lg font-medium tracking-wide">
                        {t('footerSection.copyright', { year: new Date().getFullYear() })}
                    </p>
                    <p className="text-gray-200 text-sm sm:text-base font-medium">
                        {t('footerSection.designedBy')}
                        <a
                            href="tel:+94764608225"
                            className="text-gray-200 hover:text-[#C58E2F] transition-colors duration-300 ease-in-out ml-1 hover:underline hover:underline-offset-4"
                        >
                            {t('footerSection.designedByPhone')}
                        </a>.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;